let csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  formData?: FormData;
  /** Cuerpo binario (subida del logo): se envía tal cual, con su `Content-Type`. */
  raw?: Blob;
  signal?: AbortSignal;
}

/**
 * Identificador de esta pestaña para el bloqueo de edición.
 *
 * En memoria a propósito: `sessionStorage` se copia al duplicar una pestaña, y dos pestañas con el
 * mismo identificador se bloquearían entre sí creyendo que son la misma.
 */
const holderId = (globalThis.crypto?.randomUUID?.() ?? `holder-${Math.random().toString(36).slice(2)}`);

export function editHolder(): string {
  return holderId;
}

/**
 * POST que sobrevive a que la pestaña se cierre (`pagehide`).
 *
 * Va aparte de `api()` porque necesita `keepalive` y no puede esperar respuesta, pero **sí** lleva
 * el token CSRF: sin él el servidor rechaza el POST con 403 y el bloqueo se quedaría huérfano.
 */
export function postKeepalive(path: string, body: unknown): Promise<void> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'x-edit-holder': editHolder()
  };
  if (csrfToken) headers['x-csrf-token'] = csrfToken;
  return fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers,
    body: JSON.stringify(body)
  }).then(() => undefined, () => undefined);
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json', 'x-edit-holder': editHolder() };
  // Un fichero se manda como cuerpo binario con su propio `Content-Type` (el logo de la empresa):
  // su tipo lo valida el servidor por la firma, no por lo que diga esta cabecera.
  if (options.raw) headers['Content-Type'] = options.raw.type || 'application/octet-stream';
  else if (!options.formData && options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (csrfToken && method !== 'GET') headers['x-csrf-token'] = csrfToken;

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: 'include',
      headers,
      body: options.raw ?? options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
      signal: options.signal
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'No se puede conectar con el servidor. Comprueba la red.');
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!response.ok) {
    let code = 'HTTP_ERROR';
    let message = `Error ${response.status}`;
    let details: unknown;
    if (contentType.includes('application/json')) {
      const body = await response.json().catch(() => null) as { error?: { code?: string; message?: string; details?: unknown } } | null;
      code = body?.error?.code ?? code;
      message = body?.error?.message ?? message;
      details = body?.error?.details;
    }
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('erp:unauthorized'));
    }
    throw new ApiError(response.status, code, message, details);
  }

  if (response.status === 204) return undefined as T;
  if (contentType.includes('application/json')) return response.json() as Promise<T>;
  return (await response.blob()) as T;
}

export async function apiBlob(path: string): Promise<Blob> {
  const response = await fetch(`/api${path}`, { credentials: 'include', headers: { Accept: 'application/pdf' } });
  if (!response.ok) throw new ApiError(response.status, 'DOWNLOAD_ERROR', 'No se pudo generar el documento');
  return response.blob();
}

export async function openPdf(path: string) {
  const blob = await apiBlob(path);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function downloadBlob(path: string, filename: string) {
  const blob = await apiBlob(path);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Error inesperado';
}
